'use client'

import React, { useState, useRef, useEffect, useCallback, useId } from 'react'
import { useTranslations } from 'next-intl'
import Icon from '../../atoms/Icon/Icon.component'
import type { SortOrder } from '../../../types/ResourceHub'
import type { ColorSchemeTypes } from '../../../types/ColorScheme'
import * as S from './SortSelect.styles'

export type SortSelectProps = {
  colorScheme: ColorSchemeTypes
  value: SortOrder
  onChange: (order: SortOrder) => void
  regionLabel: string
}

const SORT_OPTIONS: SortOrder[] = ['asc', 'desc']

function SortSelect({ colorScheme, value, onChange, regionLabel }: SortSelectProps) {
  const t = useTranslations('resourceHub')
  const [isOpen, setIsOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const baseId = useId()
  const listId = `${baseId}-sort-panel`
  const triggerId = `${baseId}-sort-trigger`

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (!isOpen) return

    function handleFocusIn(event: FocusEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('focusin', handleFocusIn)
    return () => document.removeEventListener('focusin', handleFocusIn)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation()
        setIsOpen(false)
        triggerRef.current?.focus()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen])

  function handleSelect(order: SortOrder) {
    onChange(order)
    setIsOpen(false)
  }

  return (
    <S.SortSelectWrapper ref={wrapperRef}>
      <S.SortSelectTrigger
        ref={triggerRef}
        id={triggerId}
        type="button"
        $isOpen={isOpen}
        colorScheme={colorScheme}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-controls={listId}
        aria-haspopup="true"
      >
        {t(`sort.${value}`)}
        <Icon name="chevron-down" aria-hidden />
      </S.SortSelectTrigger>

      {isOpen && (
        <S.SortSelectDropdown id={listId} role="region" aria-label={regionLabel}>
          {SORT_OPTIONS.map((option) => (
            <S.SortSelectOption
              key={option}
              type="button"
              $active={value === option}
              onClick={() => handleSelect(option)}
            >
              {t(`sort.${option}`)}
            </S.SortSelectOption>
          ))}
        </S.SortSelectDropdown>
      )}
    </S.SortSelectWrapper>
  )
}

export default SortSelect
